// Full survey types (post-payment, before final report)
// Primary questions: Ethnicity + ZIP Code
// Legacy fields kept for recommendation engine compatibility

export type Ethnicity =
  | 'caucasian'
  | 'black'
  | 'hispanic'
  | 'east-asian'
  | 'south-asian'
  | 'southeast-asian'
  | 'middle-eastern'
  | 'pacific-islander'
  | 'native-american'
  | 'mixed'
  | 'other'
  | 'prefer-not-to-say'

export const ETHNICITY_OPTIONS: { value: Ethnicity; label: string }[] = [
  { value: 'caucasian', label: 'White / Caucasian' },
  { value: 'black', label: 'Black / African American' },
  { value: 'hispanic', label: 'Hispanic / Latino' },
  { value: 'east-asian', label: 'East Asian (Chinese, Japanese, Korean)' },
  { value: 'south-asian', label: 'South Asian (Indian, Pakistani, Bengali)' },
  { value: 'southeast-asian', label: 'Southeast Asian (Filipino, Vietnamese, Thai)' },
  { value: 'middle-eastern', label: 'Middle Eastern / North African' },
  { value: 'pacific-islander', label: 'Native Hawaiian / Pacific Islander' },
  { value: 'native-american', label: 'Native American / Alaska Native' },
  { value: 'mixed', label: 'Mixed / Multiracial' },
  { value: 'other', label: 'Other' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
]

// Legacy types for recommendation engine compatibility
export type SkinCondition =
  | 'acne'
  | 'dryness'
  | 'oiliness'
  | 'sensitivity'
  | 'hyperpigmentation'
  | 'rosacea'
  | 'eczema'
  | 'none'

export type IncludeIngredient =
  | 'niacinamide'
  | 'retinol'
  | 'vitamin-c'
  | 'hyaluronic-acid'
  | 'salicylic-acid'
  | 'glycolic-acid'
  | 'peptides'
  | 'centella'
  | 'snail-mucin'
  | 'ceramides'
  | 'green-tea'
  | 'propolis'

export type AvoidIngredient =
  | 'fragrance'
  | 'alcohol'
  | 'essential-oils'
  | 'sulfates'
  | 'parabens'
  | 'silicones'
  | 'mineral-oil'
  | 'coconut-oil'

export type TexturePreference = 'lightweight' | 'rich' | 'no-preference'
export type RoutineLength = 'quick' | 'moderate' | 'comprehensive'
export type PregnancyStatus = 'yes' | 'no' | 'not-applicable'
export type FullSurveySectionId = 'ethnicity' | 'zipcode' | 'routineFrequency' | 'skinColor'

// Routine frequency options
export type RoutineFrequency =
  | 'just-starting'
  | 'sometimes'
  | 'most-days'
  | 'daily-habit'

export const ROUTINE_FREQUENCY_OPTIONS: { value: RoutineFrequency; label: string; emoji: string }[] = [
  { value: 'just-starting', label: "I'm just getting started with a routine", emoji: '🌱' },
  { value: 'sometimes', label: "I try to follow one, but don't always stick with it", emoji: '🌤️' },
  { value: 'most-days', label: 'I follow a routine most days', emoji: '✨' },
  { value: 'daily-habit', label: "It's already part of my daily routine", emoji: '💫' },
]

// Skin color options (Fitzpatrick scale inspired)
export type SkinColor =
  | 'very-fair'
  | 'fair'
  | 'light'
  | 'medium'
  | 'olive'
  | 'tan'
  | 'brown'
  | 'dark-brown'

export const SKIN_COLOR_OPTIONS: { value: SkinColor; label: string; color: string }[] = [
  { value: 'very-fair', label: 'Very Fair', color: '#FFE5D9' },
  { value: 'fair', label: 'Fair', color: '#FFD4C4' },
  { value: 'light', label: 'Light', color: '#E8C4A8' },
  { value: 'medium', label: 'Medium', color: '#D4A574' },
  { value: 'olive', label: 'Olive', color: '#C69C6D' },
  { value: 'tan', label: 'Tan', color: '#A67C52' },
  { value: 'brown', label: 'Brown', color: '#8B5A2B' },
  { value: 'dark-brown', label: 'Dark Brown', color: '#5C4033' },
]

// Full survey data structure
// Primary: ethnicity, zipCode (collected from user)
// Legacy: other fields use defaults for recommendation engine
export interface FullSurveyData {
  // Primary fields (collected from user)
  ethnicity: Ethnicity | null
  zipCode: string | null
  routineFrequency: RoutineFrequency | null
  skinColor: SkinColor | null

  // Legacy fields for recommendation engine (use defaults)
  currentConditions: SkinCondition[]
  includeIngredients: IncludeIngredient[]
  avoidIngredients: AvoidIngredient[]
  texturePreference: TexturePreference | null
  routineLength: RoutineLength | null
  pregnancyStatus: PregnancyStatus | null
}

// Default values for legacy fields
export const DEFAULT_FULL_SURVEY_LEGACY: Omit<FullSurveyData, 'ethnicity' | 'zipCode' | 'routineFrequency' | 'skinColor'> = {
  currentConditions: [],
  includeIngredients: [],
  avoidIngredients: [],
  texturePreference: 'no-preference',
  routineLength: 'moderate',
  pregnancyStatus: 'not-applicable',
}
